import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import { exportEventTypes } from './api/eventTypeApi'
import Button from '../../components/ui/Button'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { eventTypeColumns } from './components/EventTypeColumns'
import EventTypeFormDialog from './components/EventTypeFormDialog'
import { useEventTypes } from './hooks/useEventTypes'
import type { EventType, EventTypeUpsert } from './types'
import ReorderableLookupCards from '../../components/ui/ReorderableLookupCards'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
// Layout sınıfları (admin-page/admin-form/admin-row-actions/admin-switch) paylaşılan — 012'den yeniden kullanılır.
import '../admin/admin.css'

/**
 * Etkinlik Takip → Etkinlik Türü yönetim ekranı (023). Liste (paylaşılan ui/DataTable) + ekle/düzenle
 * (FormDialog) + durum toggle + soft-delete (confirmDialog). Başlık = menü adı (`nav.etkinlik.eventTypes`).
 * (022 StakeholdersPage deseniyle aynı; korumalı sistem kaydı yok.)
 */
export default function EventTypesPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const { list, create, update, changeStatus, remove, reorder } = useEventTypes()
  const toast = useToast()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<EventType | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)

  const rows = list.data?.content ?? []
  const submitting = create.isPending || update.isPending
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  const busyId = changeStatus.isPending
    ? (changeStatus.variables?.id ?? null)
    : remove.isPending
      ? (remove.variables ?? null)
      : null

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const openCreate = () => {
    setEditing(null)
    setNameError(null)
    setFormOpen(true)
  }
  const openEdit = (row: EventType) => {
    setEditing(row)
    setNameError(null)
    setFormOpen(true)
  }

  // Domaine özel ele alma ekranda kalır; optimistic-lock/generic toast'ı merkez (019) basar.
  const handleMutationError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setNameError(t('activity.eventTypes.errors.duplicate'))
      return
    }
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
  }

  const onSubmit = (payload: EventTypeUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            notify('success', t('activity.eventTypes.toast.updated'))
            setFormOpen(false)
          },
          onError: handleMutationError,
        },
      )
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          notify('success', t('activity.eventTypes.toast.created'))
          setFormOpen(false)
        },
        onError: handleMutationError,
      })
    }
  }

  const onToggle = (row: EventType, active: boolean) => {
    changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      {
        onSuccess: () => notify('success', t('activity.eventTypes.toast.statusChanged')),
        // Hata (optimistic-lock/generic) → merkezî toast (019).
      },
    )
  }

  // Satırı yukarı/aşağı taşı → görünen sırayı komşuyla takas edip tüm id sırasını backend'e yaz (1..N).
  const onMove = (row: EventType, dir: 'up' | 'down') => {
    const idx = rows.findIndex((r) => r.id === row.id)
    const swapWith = dir === 'up' ? idx - 1 : idx + 1
    if (idx < 0 || swapWith < 0 || swapWith >= rows.length) return
    const orderedIds = rows.map((r) => r.id)
    ;[orderedIds[idx], orderedIds[swapWith]] = [orderedIds[swapWith], orderedIds[idx]]
    reorder.mutate(orderedIds, {
      onError: () => notify('error', t('common.reorder.error')),
    })
  }

  const onDelete = (row: EventType) => {
    confirmDialog({
      header: t('activity.eventTypes.delete.title'),
      message: t('activity.eventTypes.delete.confirm', { name: row.nameTr }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('activity.eventTypes.delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', t('activity.eventTypes.toast.deleted')),
          // Hata → merkezî toast (019).
        }),
    })
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <EventTypeFormDialog
        visible={formOpen}
        initial={editing}
        submitting={submitting}
        nameError={nameError}
        onClearNameError={() => setNameError(null)}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <PageHeader
        title={t('nav.etkinlik.eventTypes')}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton onExport={exportEventTypes} disabled={rows.length === 0} />
            <Button
              icon="pi pi-plus"
              label={t('activity.eventTypes.actions.new')}
              onClick={openCreate}
            />
          </>
        }
      />

      {/* Sıralanabilir (SORT_INDEX) liste → sayfalama kapalı, kolon sıralaması yok; daima sıra düzeninde. */}
      {isMobile ? (
        <ReorderableLookupCards
          items={rows}
          loading={list.isLoading}
          busyId={busyId}
          reordering={reorder.isPending}
          onMove={onMove}
          onEdit={openEdit}
          onToggle={onToggle}
          onDelete={onDelete}
          emptyTitle={t('activity.eventTypes.empty.title')}
          emptyDescription={t('activity.eventTypes.empty.desc')}
          labels={{
            active: t('activity.eventTypes.status.active'),
            passive: t('activity.eventTypes.status.passive'),
            edit: t('activity.eventTypes.actions.edit'),
            delete: t('activity.eventTypes.actions.delete'),
            toggle: t('activity.eventTypes.actions.toggle'),
            moveUp: t('common.reorder.up'),
            moveDown: t('common.reorder.down'),
            menu: t('common.actionsMenu'),
          }}
        />
      ) : (
      <DataTable
        value={rows}
        loading={list.isLoading}
        paginator={false}
        emptyMessage={
          <EmptyState
            title={t('activity.eventTypes.empty.title')}
            description={t('activity.eventTypes.empty.desc')}
          />
        }
      >
        {eventTypeColumns({
          t,
          localized,
          busyId,
          rowCount: rows.length,
          reordering: reorder.isPending,
          onMove,
          onEdit: openEdit,
          onToggle,
          onDelete,
        })}
      </DataTable>
      )}
    </div>
  )
}
